import { useState } from 'react';

import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router';

import { useAppDispatch } from 'dux/app/hooks';

import { Box, theme } from '@prose-ui';

import { Hero } from 'Apps/LandingPages/Scenes/DensitySet/Hero';
import { InfoModal } from 'Apps/Main/Scenes/HomeV2/InfoModal';
import { HowToUse } from 'Apps/Products/Components/HowToUse';

import { IngredientsSection } from 'Components/IngredientsSlider/IngredientsSection';
import { MetaData } from 'Components/MetaData';
import Spacer from 'Components/Spacer';
import { StickyProductCTA } from 'Components/StickyProductCTA';

import { productsCategories, productsSlugs } from 'constants/products';

import { Faq } from 'Blocks/Faq';
import { Separator, SerumStep, StepSection, SupplementsStep } from 'Blocks/HairDensitySteps';
import { ScienceTeam } from 'Blocks/ScienceTeam';
import { Ticker } from 'Blocks/Ticker';

import ScrollTracker from 'Containers/ScrollTracker';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import {
  faq,
  howToUse,
  ingredientsSlider,
  scienceTeam,
  stepSerum,
  stepSupplements,
  ticker,
} from 'assets/content/densitySetLandingPage';
import { densityLPStructuredData } from 'assets/metadata/densityLPStructuredData';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent, trackPreselect } from 'dux/tracking/actions';

import { BeforeAndAfter } from './DensitySet/BeforeAndAfter';

type DensitySetLandingProps = {
  header: boolean;
  footer: boolean;
  banner: boolean;
};

export type ModalStateKey = 'modalStepSupplements' | 'modalStepSerum';

const DensitySetLanding = ({
  header = true,
  footer = true,
  banner = true,
}: DensitySetLandingProps) => {
  const dispatch = useAppDispatch();
  const [modalType, setModalType] = useState<ModalStateKey | null>(null);
  const closeModal = () => setModalType(null);
  const [heroRef, heroInView] = useInView({ threshold: 0.7, initialInView: true });
  const { isMobile, isTablet } = useResponsiveVariant({ legacy: false });
  const navigate = useNavigate();

  return (
    <>
      <MetaData
        description="Shop Prose Custom Hair Density Set — a 2-step system of Scalp Serum and Hair Supplements designed to promote healthy hair growth from inside and out."
        inheritParentCanonicalURL
        structuredData={densityLPStructuredData}
        title="The Custom Hair Density Set – Prose | Haircare"
      />
      <InfoModal
        isOpen={modalType === 'modalStepSupplements'}
        onClose={closeModal}
        tooltipContent={stepSupplements.tooltip}
      />
      <InfoModal
        isOpen={modalType === 'modalStepSerum'}
        onClose={closeModal}
        tooltipContent={stepSerum.tooltip}
      />
      <BaseScene
        background="none"
        banner={banner}
        footer={footer}
        FooterProps={{ hideCTA: true, disclaimerOnTop: true }}
        header={header}
      >
        <ScrollTracker>
          <Hero ref={heroRef} />

          <Ticker
            backgroundColor={theme.colors.primary[400]}
            color={theme.colors.neutral[200]}
            content={ticker}
            showDash={false}
          />
          <BeforeAndAfter />
          <Box
            paddingTop={{ mobile: '15x', tablet: '30x', desktop: '30x' }}
            paddingX={{ mobile: '4x', tablet: '8x' }}
            w="full"
          >
            <StepSection>
              <SupplementsStep setIsOpen={() => setModalType('modalStepSupplements')} />
              <Spacer size={32} />
              {(isMobile || isTablet) && <Separator isMobile={isMobile} />}
              {(isMobile || isTablet) && <Spacer size={32} />}
              <SerumStep setIsOpen={() => setModalType('modalStepSerum')} />
            </StepSection>
          </Box>
          <Box paddingTop={{ mobile: '15x', desktop: '30x' }}>
            <HowToUse
              amount={howToUse.amount}
              description={howToUse.description}
              posterImage={howToUse.posterImage}
              steps={howToUse.steps}
              title={howToUse.title}
              video={howToUse.video}
            />
          </Box>

          <Box
            backgroundColor="neutral.200"
            paddingTop={{ mobile: '15x', desktop: '30x' }}
            paddingX={{ mobile: 'none', tablet: 'none', desktop: '8x' }}
            w="full"
          >
            <IngredientsSection
              content={ingredientsSlider}
              trackOnChange={() => dispatch(trackHeapEvent('Ingredients Module Carousel Scroll'))}
            />
          </Box>
          <Box
            backgroundColor="neutral.200"
            paddingX={{ mobile: '6x', tablet: 'none', desktop: '8x' }}
            paddingY={{ mobile: '15x', desktop: '30x' }}
            w="full"
          >
            <ScienceTeam description={scienceTeam.description} title={scienceTeam.title} />
          </Box>
          <Box w="full">
            <Faq backgroundColor="white" questions={faq.questions} title="FAQs" />
          </Box>

          <StickyProductCTA
            customOnClick={(redirectUrl: string) =>
              dispatch(
                // @ts-expect-error dux not migrated yet
                trackPreselect({
                  productSlug: productsSlugs.SCALP_SERUM,
                  locationId: 'pdp-density_set',
                  productCategory: productsCategories.HAIRCARE,
                  onSuccess: () => {
                    dispatch(
                      // @ts-expect-error dux not migrated yet
                      trackPreselect({
                        productSlug: productsSlugs.SUPPLEMENT_CORE,
                        locationId: 'pdp-density_set',
                        // We want to make sure that the breadcrumbs call is finished before starting a cart API call
                        onSuccess: () => navigate(redirectUrl),
                        productCategory: productsCategories.HAIRCARE,
                      }),
                    );
                  },
                }),
              )
            }
            dataFrom="density-set-lp-sticky"
            productSlug={productsSlugs.SCALP_SERUM}
            shouldShow={!heroInView}
          />
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

export default DensitySetLanding;
